import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { IFakturowniaInvoiceListRow, withNetOutstanding } from 'core/hooks';
import { ChartCard, KpiCard, KpiGrid } from 'molecules';

import { MonthBarChart } from '../FinanceDashboard/charts';

interface IInvoicesSummaryProps {
  /** The already-filtered list — what the table below is rendering. */
  rows: IFakturowniaInvoiceListRow[];
}

const fmt = (n: number) => n.toFixed(2);

const isPaid = (status?: string | null) =>
  (status || '').toUpperCase() === 'PAID';

/**
 * Header-row summary above the Invoices table (Phase 9). Surfaces the four
 * numbers the financier was missing: how much was issued, paid, still owed,
 * and how much of that is overdue. Sums are net-aware (so a corrected-out
 * invoice doesn't inflate «не оплачено»). Everything reads from the same
 * filtered `rows` the table renders, so toggling direction / hideCorrected /
 * date range moves the summary in lock-step with the list.
 *
 * Phase 9.1: the page now uses `useAllFakturowniaInvoices` which walks
 * every backend page until exhausted, so these sums cover the full filtered
 * period (no `hasMore` cap, no partial-page banner).
 */
export const InvoicesSummary = memo(({ rows }: IInvoicesSummaryProps) => {
  const { t } = useTranslation();

  const netted = useMemo(() => withNetOutstanding(rows), [rows]);

  const issuedSum = useMemo(
    () => netted.reduce((s, r) => s + (r.priceGross || 0), 0),
    [netted],
  );
  const paidRows = useMemo(() => netted.filter((r) => isPaid(r.status)), [
    netted,
  ]);
  const paidSum = useMemo(
    () => paidRows.reduce((s, r) => s + (r.paid || 0), 0),
    [paidRows],
  );
  const outstandingRows = useMemo(
    () => netted.filter((r) => r.netOutstanding > 0),
    [netted],
  );
  const outstandingSum = useMemo(
    () => outstandingRows.reduce((s, r) => s + r.netOutstanding, 0),
    [outstandingRows],
  );
  const overdueRows = useMemo(() => {
    const todayMs = moment().startOf('day').valueOf();
    return outstandingRows.filter(
      (r) => r.paymentTo && moment(r.paymentTo).valueOf() < todayMs,
    );
  }, [outstandingRows]);
  const overdueSum = useMemo(
    () => overdueRows.reduce((s, r) => s + r.netOutstanding, 0),
    [overdueRows],
  );

  // Group netted rows by YYYY-MM for the trend bar chart.
  const trend = useMemo(() => {
    const buckets = new Map<string, number>();
    netted.forEach((r) => {
      if (!r.issueDate) return;
      const key = moment(r.issueDate).format('YYYY-MM');
      buckets.set(key, (buckets.get(key) || 0) + (r.priceGross || 0));
    });
    const labels: string[] = [];
    const values: number[] = [];
    Array.from(buckets.keys())
      .sort((a, b) => moment(a).valueOf() - moment(b).valueOf())
      .forEach((k) => {
        labels.push(k);
        values.push(buckets.get(k) || 0);
      });
    return { labels, values };
  }, [netted]);

  const countSum = (count: number, sum: number) =>
    t('pages.fakturownia.invoices.summary.countSum', {
      count,
      sum: fmt(sum),
    });

  const showTrend = trend.labels.length >= 2;

  return (
    <div style={{ margin: '4px 0 12px' }}>
      <KpiGrid columns={4}>
        <KpiCard
          label={t('pages.fakturownia.invoices.summary.issued')}
          value={countSum(netted.length, issuedSum)}
        />
        <KpiCard
          label={t('pages.fakturownia.invoices.summary.paid')}
          value={countSum(paidRows.length, paidSum)}
          accent="#16a34a"
        />
        <KpiCard
          label={t('pages.fakturownia.invoices.summary.outstanding')}
          value={countSum(outstandingRows.length, outstandingSum)}
          accent={outstandingRows.length > 0 ? '#d97706' : undefined}
        />
        <KpiCard
          label={t('pages.fakturownia.invoices.summary.overdue')}
          value={countSum(overdueRows.length, overdueSum)}
          accent={overdueRows.length > 0 ? '#dc2626' : undefined}
        />
      </KpiGrid>

      {showTrend ? (
        <ChartCard title={t('pages.fakturownia.invoices.summary.trendTitle')}>
          <MonthBarChart labels={trend.labels} values={trend.values} />
        </ChartCard>
      ) : null}
    </div>
  );
});
